/**
 * NL-041 / NL-010 / NL-014 advisory-surface flags.
 *
 * These are the operator kill switches for the three reactivated planning
 * capabilities. They are read on the server and passed into the wizard as
 * props, so no pricing or evidence source is ever reachable from the client and
 * no `NEXT_PUBLIC_` value has to be trusted.
 *
 * All three default to OFF. Absence of configuration must never activate an
 * advisory surface, and an unparseable value falls back to OFF rather than
 * guessing. The governance amendment
 * `docs/next-level-implementation/OWNER-PORTFOLIO-REACTIVATION-2026-08-11.json`
 * represents all three as intentionally default-off, and that claim is only
 * true because this module defaults them off.
 *
 * None of these flags authorizes a binding quote, a payment, a provider call, a
 * CRM write, or a message. Turning every one of them on changes what the planner
 * explains, never what it does.
 */

export const ADVISORY_FLAG_ENV_KEYS = {
  packageRecommendationEnabled: "FUNNEL_PACKAGE_RECOMMENDATION_ENABLED",
  seasonalDemandEnabled: "FUNNEL_SEASONAL_DEMAND_ENABLED",
  calculatorBenchmarkEnabled: "FUNNEL_CALCULATOR_BENCHMARK_ENABLED",
} as const;

export type FunnelAdvisoryFlags = {
  /** NL-041: the Product Library package suggestion panel. */
  readonly packageRecommendationEnabled: boolean;
  /** NL-010: manual seasonal planning scenarios in Projection. */
  readonly seasonalDemandEnabled: boolean;
  /** NL-014: the calculation explanation and receipt disclosure. */
  readonly calculatorBenchmarkEnabled: boolean;
};

export const ALL_ADVISORY_FLAGS_OFF: FunnelAdvisoryFlags = Object.freeze({
  packageRecommendationEnabled: false,
  seasonalDemandEnabled: false,
  calculatorBenchmarkEnabled: false,
});

type FlagEnv = Readonly<Record<string, string | undefined>>;

const TRUE_VALUES = new Set(["1", "true", "yes", "on", "enabled"]);

/** Fails closed: only an explicit affirmative enables a surface. */
function enabled(env: FlagEnv, key: string): boolean {
  return TRUE_VALUES.has(env[key]?.trim().toLowerCase() ?? "");
}

export function getFunnelAdvisoryFlags(env: FlagEnv = process.env): FunnelAdvisoryFlags {
  return {
    packageRecommendationEnabled: enabled(env, ADVISORY_FLAG_ENV_KEYS.packageRecommendationEnabled),
    seasonalDemandEnabled: enabled(env, ADVISORY_FLAG_ENV_KEYS.seasonalDemandEnabled),
    calculatorBenchmarkEnabled: enabled(env, ADVISORY_FLAG_ENV_KEYS.calculatorBenchmarkEnabled),
  };
}
